import { getUserIdsFromContacts } from './contact/contactutils';
import { isNilOrEmpty, removeTypenameFromObject } from './objectUtils';
import {
  CreateFilterGroupInput,
  FilterEntityType,
  FilterGroup,
  FilterGroupDetails,
  FilterGroupDetailsDTO,
  FilterGroupType,
  UpdateFilterGroupInput,
  User,
} from './types';

export interface ClassifiedManagementFilters {
  buildingIds: string[];
  ownerIds: string[];
  unitIds: string[];
}

const classifyManagementFilterEntitiesIds = (filterGroup: FilterGroup) => {
  const initialEntityIds = {
    buildingIds: [],
    ownerIds: [],
    unitIds: [],
  };
  if (filterGroup.type !== FilterGroupType.MANAGEMENT) {
    return initialEntityIds;
  }
  const classification = filterGroup.filterGroupDetails.reduce<ClassifiedManagementFilters>(
    (acc, filterGroupDetail) => {
      const entityIds = filterGroupDetail.filterEntityValues ?? [];
      switch (filterGroupDetail.filterEntityType) {
        case FilterEntityType.BUILDING:
          acc.buildingIds = entityIds;
          break;
        case FilterEntityType.OWNER:
          acc.ownerIds = entityIds;
          break;
        case FilterEntityType.UNIT:
          acc.unitIds = entityIds;
          break;
        default:
          break;
      }
      return acc;
    },
    initialEntityIds
  );
  return classification;
};

export const classifyManagementFiltersEntitiesIds = (filterGroups?: FilterGroup[]) => {
  const initialEntityIds = {
    buildingIds: [],
    ownerIds: [],
    unitIds: [],
  };
  if (isNilOrEmpty(filterGroups)) {
    return initialEntityIds;
  }
  const classification = filterGroups.reduce<ClassifiedManagementFilters>((acc, filterGroup) => {
    const newClassification = classifyManagementFilterEntitiesIds(filterGroup);
    acc.buildingIds = [...acc.buildingIds, ...newClassification.buildingIds];
    acc.ownerIds = [...acc.ownerIds, ...newClassification.ownerIds];
    acc.unitIds = [...acc.unitIds, ...newClassification.unitIds];
    return acc;
  }, initialEntityIds);
  return classification;
};

const getFilterEntityValues = (
  filterGroup: FilterGroup,
  filterEntityType: FilterEntityType,
  filterEntityField: string
) =>
  filterGroup.filterGroupDetails.reduce<string[]>((acc, filterGroupDetail) => {
    if (
      filterGroupDetail.filterEntityType === filterEntityType &&
      filterGroupDetail.filterEntityField === filterEntityField
    ) {
      acc.push(...(filterGroupDetail.filterEntityValues ?? []));
    }
    return acc;
  }, []);

export const getFilterEntityValuesForGroups = (
  filterGroups: FilterGroup[],
  filterEntityType: FilterEntityType,
  filterEntityField: string
) => filterGroups.flatMap((filterGroup) => getFilterEntityValues(filterGroup, filterEntityType, filterEntityField));

interface FilterGroupContextFunctions {
  createFilterGroup: (input: Omit<CreateFilterGroupInput, 'clientId' | 'readId'>) => Promise<FilterGroup>;
  updateFilterGroup: (updates: UpdateFilterGroupInput) => Promise<FilterGroup>;
}

interface EntitiesIds {
  unitIds: string[];
  buildingIds: string[];
  ownerIds: string[];
}

const getEntityIdsForFilterType = (entitiesIds: EntitiesIds, filterEntityType: FilterEntityType) => {
  switch (filterEntityType) {
    case FilterEntityType.BUILDING: {
      return entitiesIds.buildingIds;
    }
    case FilterEntityType.OWNER: {
      return entitiesIds.ownerIds;
    }
    case FilterEntityType.UNIT: {
      return entitiesIds.unitIds;
    }
    default:
      return null;
  }
};

const upsertEntitiesIdsInManagementFilters = (
  allManagementFilters: FilterGroup[],
  userIdsToAdd: string[],
  userIdsToRemove: string[],
  entitiesIdsToAdd: EntitiesIds,
  entitiesIdsToRemove: EntitiesIds,
  managementFiltersFunctions: FilterGroupContextFunctions
) =>
  [...userIdsToAdd, ...userIdsToRemove].map((userId) => {
    const existing = allManagementFilters.find((f) => f.userId === userId);
    if (existing) {
      const newDetails = existing.filterGroupDetails.map((detail) => {
        const clean = removeTypenameFromObject(detail as unknown as FilterGroupDetailsDTO);
        const toAdd = getEntityIdsForFilterType(entitiesIdsToAdd, clean.filterEntityType) ?? [];
        const toRemove = getEntityIdsForFilterType(entitiesIdsToRemove, clean.filterEntityType) ?? [];
        const base = clean.filterEntityValues ?? [];

        const shouldAdd = userIdsToAdd.includes(userId);
        if (shouldAdd) {
          const filtered = base.filter((id) => !toAdd.includes(id));
          return { ...clean, filterEntityValues: [...filtered, ...toAdd] };
        }
        const filtered = base.filter((id) => !toRemove.includes(id));
        return { ...clean, filterEntityValues: filtered };
      });

      return managementFiltersFunctions.updateFilterGroup({
        id: existing.id,
        _version: existing._version,
        filterGroupDetails: newDetails,
      });
    }

    const newDetails: FilterGroupDetails[] = [
      {
        filterEntityType: FilterEntityType.BUILDING,
        filterEntityField: 'id',
        filterEntityValues: entitiesIdsToAdd.buildingIds,
      },
      {
        filterEntityType: FilterEntityType.OWNER,
        filterEntityField: 'id',
        filterEntityValues: entitiesIdsToAdd.ownerIds,
      },
      {
        filterEntityType: FilterEntityType.UNIT,
        filterEntityField: 'id',
        filterEntityValues: entitiesIdsToAdd.unitIds,
      },
    ];

    return managementFiltersFunctions.createFilterGroup({
      filterGroupDetails: newDetails,
      userId,
      type: FilterGroupType.MANAGEMENT,
    });
  });

export const updateOrCreateManagementFilters = async (
  allManagementFilters: FilterGroup[],
  initialSelectedMemberIds: string[],
  selectedMemberIds: string[],
  entitiesIdsToAdd: EntitiesIds,
  entitiesIdsToRemove: EntitiesIds,
  allUsers: User[],
  managementFiltersFunctions: FilterGroupContextFunctions
) => {
  const initialUserIds = getUserIdsFromContacts(initialSelectedMemberIds, allUsers);
  const userIdsToAdd = getUserIdsFromContacts(selectedMemberIds, allUsers);
  const userIdsToRemove = initialUserIds.filter((userId) => !userIdsToAdd.includes(userId));

  return await Promise.all(
    upsertEntitiesIdsInManagementFilters(
      allManagementFilters,
      userIdsToAdd,
      userIdsToRemove,
      entitiesIdsToAdd,
      entitiesIdsToRemove,
      managementFiltersFunctions
    )
  );
};
